import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  TextInput,
  Layer,
  Spinner,
  Notification,
} from "grommet";
import { Search, Add, Info } from "grommet-icons";
import adminAxios from "../../config/adminAxios";
import SkillQuestionsTable from "../../components/AdminPortal/SkillQuestionsTable";
import { useNavigate, useParams } from "react-router-dom";
import { HeaderNavigation } from "../../components/Headers/AdminHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";

const SkillQuestionsPage = () => {
  const { portfolio_id, skillId } = useParams();
  const navigate = useNavigate();

  // existing state
  const [questions, setQuestions] = useState([]);
  const [portfolioName, setPortfolioName] = useState("");
  const [skillName, setSkillName] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [publishedCount, setPublishedCount] = useState(0);
  const [unpublishedCount, setUnpublishedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [notification, setNotification] = useState(null);

  // NEW: control modal visibility
  const [confirmUnpublishOpen, setConfirmUnpublishOpen] = useState(false);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);

  useEffect(() => {
    const fetchQuestionsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await adminAxios.get(
          `/api/admin/questions-skill/${portfolio_id}/${skillId}`,
        );
        setQuestions(data.questions || []);
        setPortfolioName(data.portfolioName || "");
        setSkillName(data.skillName || "");
        setTotalCount(data.total ?? data.questions.length);
        setPublishedCount(data.published ?? 0);
        setUnpublishedCount(data.unpublished ?? 0);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Error fetching questions",
        );
      } finally {
        setLoading(false);
      }
    };

    if (skillId) fetchQuestionsData();
  }, [portfolio_id, skillId]);

  const newQuestionsExist = totalCount > publishedCount;

  const filteredQuestions = questions.filter((q) =>
    q.question.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // replaced immediate unpublish with opening the confirmation modal
  const handleUnpublishClick = () => setConfirmUnpublishOpen(true);
  const handlePublishClick = () => setConfirmPublishOpen(true);

  const handleConfirmUnpublish = async () => {
    // close modal, show spinner
    setConfirmUnpublishOpen(false);
    setLoading(true);

    try {
      // gather only currently published question IDs
      const idsToUnpublish = questions
        .filter((q) => q.status === 1)
        .map((q) => q.id);

      // fire off the patch
      await adminAxios.patch("/api/admin/unpublish-questions", {
        ids: idsToUnpublish,
      });

      // show success toast and reload
      setNotification({
        message: "Questions have been unpublished.",
        status: "normal",
      });
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      // on error, show toast and un-blank the screen
      setNotification({
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to unpublish questions",
        status: "critical",
      });
      setLoading(false);
    }
  };

  const handleConfirmPublish = async () => {
    // close modal, show spinner
    setConfirmPublishOpen(false);
    setLoading(true);

    try {
      // gather only currently unpublished question IDs
      const idsToPublish = questions
        .filter((q) => q.status === 0)
        .map((q) => q.id);

      // fire off the patch
      await adminAxios.patch("/api/admin/publish-questions", { ids: idsToPublish });

      // show success toast and reload
      setNotification({
        message: "Questions have been published.",
        status: "normal",
      });
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      // on error, show toast and un-blank the screen
      setNotification({
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to publish questions",
        status: "critical",
      });
      setLoading(false);
    }
  };

  return (
    <Box fill direction="column">
      <HeaderNavigation />

      <BreadcrumbNavigation
        breadcrumbs={[
          {
            label: "Skill Question Mapping",
            link: `/admin/portfolio/${portfolio_id}/skill-question`,
          },
          {
            label: "Questions",
            link: `/admin/portfolio/${portfolio_id}/skill-question/${skillId}`,
          },
        ]}
      />

      <Box height="2px" background="brand" margin={{ vertical: "small" }} />

      <Box
        flex
        direction="column"
        pad={{ horizontal: "medium", bottom: "medium", top: "small" }}
        gap="medium"
        overflow="auto"
      >
        {loading ? (
          <Box fill align="center" justify="center">
            <Spinner />
          </Box>
        ) : error ? (
          <Box fill align="center" justify="center">
            <Text color="status-critical">{error}</Text>
          </Box>
        ) : (
          <>
            <Box
              direction="row"
              justify="between"
              align="center"
              margin={{ bottom: "small" }}
            >
              <Box>
                <Text size="large" weight="bold" margin={{ bottom: "xsmall" }}>
                  {portfolioName} – {skillName}
                </Text>
                <Box direction="row" gap="medium">
                  <Box direction="row" align="center" gap="xsmall">
                    <Box
                      width="10px"
                      height="10px"
                      round="full"
                      background="status-ok"
                    />
                    <Text size="small">{publishedCount} Published</Text>
                  </Box>
                  <Box direction="row" align="center" gap="xsmall">
                    <Box
                      width="10px"
                      height="10px"
                      round="full"
                      background="status-critical"
                    />
                    <Text size="small">{unpublishedCount} Unpublished</Text>
                  </Box>
                </Box>
                <Text>{totalCount} total questions</Text>
              </Box>
              <Box direction="row" gap="small">
                <Button
                  label="Unpublish"
                  secondary
                  onClick={handleUnpublishClick}
                  disabled={publishedCount === 0}
                  title="Unpublish all questions"
                />
                <Button
                  label="Publish Changes"
                  secondary
                  onClick={handlePublishClick}
                  title="Publish all questions"
                  disabled={!newQuestionsExist || unpublishedCount === 0}
                />
                <Button
                  icon={<Add />}
                  label="Add Question"
                  primary
                  onClick={() =>
                    navigate(
                      `/admin/portfolio/${portfolio_id}/skill-question/${skillId}/add-question`,
                    )
                  }
                  title="Add a new question"
                />
              </Box>
            </Box>

            <TextInput
              icon={<Search />}
              placeholder="Search questions"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <Box
              border={{ color: "light-4", size: "small" }}
              round="small"
              pad="medium"
              margin={{ top: "small" }}
              flex
              overflow="auto"
            >
              <SkillQuestionsTable
                questions={filteredQuestions}
                setNotification={setNotification}
                showStatusDot
              />
            </Box>
          </>
        )}
      </Box>

      <StickyFooter />

      {notification && (
        <Notification
          toast
          message={notification.message}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Unpublish Confirmation Modal */}
      {confirmUnpublishOpen && (
        <Layer
          onEsc={() => setConfirmUnpublishOpen(false)}
          onClickOutside={() => setConfirmUnpublishOpen(false)}
          responsive={false}
        >
          <Box pad="medium" gap="medium" width="medium">
            <Box direction="row" align="center" gap="small">
              <Info color="brand" />
              <Text weight="bold">Unpublish Questions</Text>
            </Box>
            <Text>
              You are about to unpublish all the published questions for{" "}
              <strong>
                {portfolioName} – {skillName}.
              </strong>
              <br />
              Total published questions: {publishedCount}.<br />
              These questions will no longer be visible to end-users. Are you
              sure you want to proceed?
            </Text>
            <Box direction="row" justify="end" gap="small">
              <Button
                label="No"
                onClick={() => setConfirmUnpublishOpen(false)}
              />
              <Button label="Yes" primary onClick={handleConfirmUnpublish} />
            </Box>
          </Box>
        </Layer>
      )}

      {/* Publish Confirmation Modal */}
      {confirmPublishOpen && (
        <Layer
          onEsc={() => setConfirmPublishOpen(false)}
          onClickOutside={() => setConfirmPublishOpen(false)}
          responsive={false}
        >
          <Box pad="medium" gap="medium" width="large" height="auto">
            <Box direction="row" align="center" gap="small">
              <Text weight="bold">Publish Questions</Text>
            </Box>
            <Text>
              You are about to publish all the unpublished questions for <br />
              <strong>
                {portfolioName} - {skillName}
              </strong>
              . Total unpublished questions: {unpublishedCount}.
            </Text>
            <Box
              style={{ maxHeight: "200px" }}
              overflow={{ vertical: "auto" }}
              border={{ color: "light-4" }}
              pad="small"
            >
              {questions
                .filter((q) => q.status === 0)
                .map((q) => (
                  <Text key={q.id} size="small" margin={{ bottom: "xsmall" }}>
                    • {q.question}
                  </Text>
                ))}
            </Box>
            <Box direction="row" justify="end" gap="small">
              <Button label="No" onClick={() => setConfirmPublishOpen(false)} />
              <Button label="Yes" primary onClick={handleConfirmPublish} />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default SkillQuestionsPage;
